import { useCallback, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import { ScheduleRecognitionPeriod } from "@common/schemas/domains/accounting/scheduleReview.schema";
import { Task } from "@common/types/task";
import {
    ScheduleRecognitionReviewColumn,
    ScheduleRecognitionReviewView,
} from "@common/types/views/scheduleRecognitionReviewView";
import { ViewType } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { TablePageSubHeader } from "@design-system/TablePageSubHeader/TablePageSubHeader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useConfirmAllRecognitionPeriodsMutation,
    useConfirmRecognitionPeriodsMutation,
    useRecognitionReviewPeriods,
} from "@api/scheduleReview.api";
import { RecognitionReviewTaskActionBar } from "./RecognitionReviewTaskActionBar";
import {
    formatPeriodKeyLabel,
    getRecognitionPeriodKey,
} from "./recognitionReviewHelpers";
import { recognitionReviewViewConfig } from "./recognitionReviewViewConfig";
import { SCHEDULE_REVIEW_TOAST_KEY } from "./scheduleReview.constants";
import { getSelectedRowIds } from "./scheduleReviewSelection";
import styles from "./ScheduleReviewTaskBody.module.scss";

interface RecognitionReviewTaskBodyProps {
    selectedTask: Task;
}

const recognitionView: ScheduleRecognitionReviewView = {
    type: ViewType.SCHEDULE_RECOGNITION_REVIEW,
    options: { bulkActions: true, selection: true, alwaysShowSelection: true },
    columns: [
        { id: ScheduleRecognitionReviewColumn.DATE },
        { id: ScheduleRecognitionReviewColumn.SCHEDULE },
        { id: ScheduleRecognitionReviewColumn.SOURCE },
        { id: ScheduleRecognitionReviewColumn.COUNTERPARTY },
        { id: ScheduleRecognitionReviewColumn.HOLDING_GL },
        { id: ScheduleRecognitionReviewColumn.RECOGNITION_GL },
        { id: ScheduleRecognitionReviewColumn.PERIOD },
        { id: ScheduleRecognitionReviewColumn.AMOUNT },
    ],
    filters: {},
};

/**
 * Body of a `revenue_recognition_review` task: the pending recognition
 * periods of one entity for one month, confirmed one by one or all at once.
 * The month comes from the task metadata (or its system key); the rows come
 * from the review endpoint, not from a persisted view.
 */
export const RecognitionReviewTaskBody = ({
    selectedTask,
}: RecognitionReviewTaskBodyProps) => {
    const entityId = selectedTask.entityIds?.[0] ?? null;
    const periodKey = getRecognitionPeriodKey(selectedTask);
    const { toast, error } = useToaster();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const { data, isLoading, isError } = useRecognitionReviewPeriods({
        entityId,
        periodKey,
    });
    const confirmSelectedMutation = useConfirmRecognitionPeriodsMutation();
    const confirmAllMutation = useConfirmAllRecognitionPeriodsMutation();

    const periods = useMemo(() => data?.periods ?? [], [data]);
    const selectedPeriodIds = useMemo(
        () => getSelectedRowIds(rowSelection),
        [rowSelection],
    );

    const reportResult = useCallback(
        (result: {
            postedPeriodIds: string[];
            skippedLockedPeriodIds: string[];
            failed: Array<{ message: string }>;
        }) => {
            const posted = result.postedPeriodIds.length;
            const skipped = result.skippedLockedPeriodIds.length;
            if (posted === 0) {
                error(
                    result.failed[0]?.message ??
                        (skipped > 0
                            ? `${pluralizeWithCount("period", skipped)} in a locked period, nothing posted`
                            : "Nothing was posted"),
                );
                return;
            }
            toast(
                `Posted ${pluralizeWithCount("recognition entry", posted)}` +
                    (skipped > 0
                        ? `, ${pluralizeWithCount("period", skipped)} locked and left pending`
                        : ""),
                SCHEDULE_REVIEW_TOAST_KEY,
            );
        },
        [error, toast],
    );

    const handleConfirmSelected = useCallback(async () => {
        if (
            entityId == null ||
            periodKey == null ||
            selectedPeriodIds.length === 0
        ) {
            return;
        }
        try {
            const result = await confirmSelectedMutation.mutateAsync({
                entityId,
                periodKey,
                periodIds: selectedPeriodIds,
            });
            setRowSelection({});
            reportResult(result);
        } catch (confirmError) {
            error(
                confirmError instanceof Error
                    ? confirmError.message
                    : "Failed to post recognition",
            );
        }
    }, [
        confirmSelectedMutation,
        entityId,
        error,
        periodKey,
        reportResult,
        selectedPeriodIds,
    ]);

    const handleConfirmAll = useCallback(async () => {
        if (entityId == null || periodKey == null) {
            return;
        }
        try {
            const result = await confirmAllMutation.mutateAsync({
                entityId,
                periodKey,
            });
            setRowSelection({});
            reportResult(result);
        } catch (confirmError) {
            error(
                confirmError instanceof Error
                    ? confirmError.message
                    : "Failed to post recognition",
            );
        }
    }, [confirmAllMutation, entityId, error, periodKey, reportResult]);

    if (entityId == null || periodKey == null) {
        return (
            <EmptyState
                header="This task is missing its entity or month"
                body="Mark this task done. If recognition is still due, a new task appears after the next daily sync."
            />
        );
    }
    if (isError) {
        return (
            <EmptyState
                header="Could not load recognition periods"
                body="Try again in a moment."
            />
        );
    }

    const periodLabel = formatPeriodKeyLabel(periodKey);
    const postedCount = data?.postedCount ?? 0;
    const isBusy =
        confirmSelectedMutation.isPending || confirmAllMutation.isPending;

    return (
        <div className={styles.container}>
            {data && postedCount + periods.length > 0 && (
                <TablePageSubHeader borderBottom>
                    {postedCount + periods.length === postedCount
                        ? `All ${postedCount} posted for ${periodLabel}`
                        : `Posted ${postedCount} of ${postedCount + periods.length} for ${periodLabel}`}
                </TablePageSubHeader>
            )}
            <section className={styles.body}>
                <ViewTable<
                    ScheduleRecognitionPeriod,
                    ScheduleRecognitionReviewColumn,
                    never
                >
                    data={isLoading ? null : periods}
                    view={recognitionView}
                    config={recognitionReviewViewConfig}
                    getRowId={(row) => row.id}
                    enableRowSelection
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    loadingView={() => <Loader />}
                    emptyView={() => (
                        <EmptyState
                            header={`No pending recognition for ${periodLabel}`}
                            body="Everything due this month is posted. Mark the task done when you are finished."
                        />
                    )}
                    footer={
                        <RecognitionReviewTaskActionBar
                            periodLabel={periodLabel}
                            selectedCount={selectedPeriodIds.length}
                            totalCount={periods.length}
                            isBusy={isBusy}
                            onConfirmSelected={() =>
                                void handleConfirmSelected()
                            }
                            onConfirmAll={() => void handleConfirmAll()}
                        />
                    }
                />
            </section>
        </div>
    );
};
